import { onMount, Motion, Show } from "zyra";
import { getPage, pages, type Page } from "../content";
import counterCode from "../examples/counter.tsx?raw";
import { DocLink } from "./DocLink";
import { CodeBlock } from "./CodeBlock";
import { CounterDemo } from "./CounterDemo";
import { Markdown } from "./Markdown";
import { TableOfContents } from "./TableOfContents";
import { UIShowcase } from "./UIShowcase";
import type { SiteState } from "../app/site-state";
export function DocumentPage(props: { site: SiteState; page: Page }) {
  const site = props.site;
  const page = props.page;
  const index = pages.indexOf(page);
  onMount(() => {
    if (location.hash)
      document
        .getElementById(decodeURIComponent(location.hash.slice(1)))
        ?.scrollIntoView();
  });
  return (
    <>
      <article class={"document " + (!page.slug ? "overview" : "")}>
        <div class="breadcrumb">
          Documentation <span>/</span> {page.group}
        </div>
        <Motion.div
          enter={{ opacity: 0, y: 12 }}
          transition={{ duration: 350 }}
        >
          <div class="eyebrow">
            <span />
            {!page.slug ? "THE ZYRA FRAMEWORK" : page.group.toUpperCase()}
          </div>
          <h1 tabIndex={-1}>
            {!page.slug ? (
              <>
                Build with <span>less friction.</span>
              </>
            ) : (
              page.title
            )}
          </h1>
          <p class="description">{page.description}</p>
        </Motion.div>
        <Show when={!page.slug}>
          <div class="hero-actions">
            <DocLink
              site={site}
              page={getPage("installation")}
              class="primary-button"
            >
              Start building <span>↗</span>
            </DocLink>
            <DocLink
              site={site}
              page={getPage("first-component")}
              class="text-button"
            >
              Your first component →
            </DocLink>
          </div>
          <div class="release-note">
            <span class="release-icon">◈</span>
            <p>
              <strong>0.1 · Release candidate</strong>
              <br />
              An independent foundation, built in the open.
            </p>
            <DocLink site={site} page={getPage("validation")}>
              Release checks ↗
            </DocLink>
          </div>
        </Show>
        <Show when={!!page.demo}>
          <CounterDemo />
        </Show>
        <Show when={!page.slug}>
          <div class="quick-links">
            {[
              {
                page: getPage("reactivity"),
                icon: "⌁",
                label: "Think in signals",
                detail: "Update only what changes.",
              },
              {
                page: getPage("collections"),
                icon: "▤",
                label: "Keep it local",
                detail: "Your data. On your device.",
              },
              {
                page: getPage("motion"),
                icon: "↗",
                label: "Make it move",
                detail: "From a state to a feeling.",
              },
            ].map((item) => (
              <DocLink site={site} page={item.page} class="quick-link">
                <span class="quick-icon">{item.icon}</span>
                <strong>{item.label}</strong>
                <small>{item.detail}</small>
                <span class="quick-arrow">↗</span>
              </DocLink>
            ))}
          </div>
        </Show>
        <Show when={page.slug === "ui"}>
          <UIShowcase />
        </Show>
        <Markdown site={site} body={page.body} />
        <Show when={!page.slug}>
          <CodeBlock language="tsx" text={counterCode} />
        </Show>
        <div class="page-feedback">
          <span>Built with Zyra. Documented with care.</span>
          <span>v0.1 / TSX</span>
        </div>
        <nav class="page-pagination" aria-label="Adjacent pages">
          {index > 0 ? (
            <DocLink site={site} page={pages[index - 1]}>
              <small>← PREVIOUS</small>
              <strong>{pages[index - 1].title}</strong>
            </DocLink>
          ) : (
            <span />
          )}
          {index < pages.length - 1 ? (
            <DocLink site={site} page={pages[index + 1]}>
              <small>NEXT →</small>
              <strong>{pages[index + 1].title}</strong>
            </DocLink>
          ) : null}
        </nav>
        <footer class="document-footer">
          <span>© {new Date().getFullYear()} Zyra. MIT licensed.</span>
          <a href="#top">Back to top ↑</a>
        </footer>
      </article>
      <TableOfContents site={site} page={page} />
    </>
  );
}
